import { AutoConfirmation } from 'app/components/AutoLoan/AutoConfirmation';
import { CurrencyAllowZero } from 'app/components/FormFields/CurrencyAllowZero';
import { Mileage } from 'app/components/FormFields/Mileage';
import { RadioField } from 'app/components/FormFields/RadioField';
import { Select } from 'app/components/FormFields/Select';
import { FieldNames } from 'app/models/fields/names';
import { DESIRED_LOAN_TERM_OPTIONS, VEHICLE_PURCHASE_PLAN_OPTIONS } from 'app/models/options/options';
import { SHARED } from 'app/models/questions/shared';
import { Question } from 'app/models/types';
import { Currency } from '../../components/FormFields/Currency';
import { TextInput } from '../../components/FormFields/TextInput';
import { isFromDealer } from '../fields/conditionals';
import { AUTO_LOAN_INQUIRY_FINAL_PAGE_QUESTION } from '../sections/constants';
import { Year } from 'app/components/FormFields/Year';

/**
 * Takes in a field name and returns the corresponding auto loan
 * question
 *
 * @param {string} name Field name
 * @returns {Question} Question with matching field name
 */
export const autoLoanInquiryQuestionByName = (name: string): Question => {
  return AUTO_LOAN_INQUIRY_QUESTIONS.find((question) => {
    return question.name === name;
  }) as Question;
};

/**
 * Takes in an array of field names and returns an array of questions
 *
 * @param {string[]} [names=[]] Field names
 * @returns {Question[]} Questions matching field names
 */
export const autoLoanInquiryQuestionsByName = (names: string[] = []): Question[] => {
  const questions: Question[] = [];
  for (const name of names) {
    questions.push(autoLoanInquiryQuestionByName(name));
  }
  return questions;
};

export const autoLoanInquiryQuestionById = (id: number): Question => {
  return AUTO_LOAN_INQUIRY_QUESTIONS.find((question) => question.id === id) as Question;
};

const AUTO_LOAN_INQUIRY_QUESTIONS: Question[] = [
  {
    id           : 1001,
    component    : RadioField,
    name         : FieldNames.autoPurchasePlan,
    options      : VEHICLE_PURCHASE_PLAN_OPTIONS,
  },
  {
    id           : 1002,
    component    : Currency,
    label        : 'Purchase Price',
    name         : FieldNames.purchasePrice,
    title        : 'What is the purchase price?',
    popover      : 'Enter the total price you expect to pay for your vehicle.',
  },
  {
    id           : 1003,
    component    : Currency,
    label        : 'Trade In Value',
    name         : FieldNames.tradeInValue,
    showIf       : [isFromDealer],
    title        : 'If you have a trade-in, what is the expected value?',
    isOptional   : true,
    popover      : 'If you are working with a dealer and expect to trade-in your current vehicle, then enter the amount you expect to receive for it.',
    validator    : {
      type: 'isAutoLoanValid',
      param: FieldNames.tradeInValue,
    },
  },
  {
    id           : 1004,
    component    : CurrencyAllowZero,
    label        : 'Down Payment',
    name         : FieldNames.downDollar,
    title        : 'What is your down payment?',
    isOptional   : true,
    popover      : 'Enter the amount of money you expect to put down for the vehicle purchase, separate from the trade-in value.',
    validator    : {
      type: 'isAutoLoanValid',
      param: FieldNames.downDollar,
    },
  },
  {
    id           : 1005,
    component    : Select,
    name         : FieldNames.loanTermDesired,
    options      : DESIRED_LOAN_TERM_OPTIONS,
    title        : 'What is your desired loan term/length (in months)?',
  },
  {
    id: 1006,
    ...SHARED[FieldNames.firstName],
  },
  {
    id: 1007,
    ...SHARED[FieldNames.middleName],
  },
  {
    id: 1008,
    ...SHARED[FieldNames.lastName],
  },
  {
    id: 1009,
    ...SHARED[FieldNames.suffixName],
  },
  {
    id: 1010,
    ...SHARED[FieldNames.email],
  },
  {
    id: 1011,
    ...SHARED[FieldNames.phone],
  },
  {
    id: 1012,
    ...SHARED[FieldNames.dateOfBirth],
  },
  {
    id          : AUTO_LOAN_INQUIRY_FINAL_PAGE_QUESTION,
    component   : AutoConfirmation,
    name        : 'FinalPage',
  },
  {
    id           : 1013,
    component    : TextInput,
    label        : 'Vehicle Make',
    name         : FieldNames.vehicleMake,
  },
  {
    id           : 1014,
    component    : TextInput,
    label        : 'Vehicle Model',
    name         : FieldNames.vehicleModel,
  },
  {
    id         : 1015,
    component  : Mileage,
    label      : 'Estimated Mileage',
    name       : FieldNames.vehicleMileage,
    isOptional : true,
  },
  {
    id        : 1016,
    component : Year,
    name      : FieldNames.vehicleYear,
    label     : 'Vehicle Year',
  },
];
